import { GlobalCopilotFloatieButton } from '@ms/yammer-copilot/dist/integration';
import {
  ResizableLeftNavProvider,
  useBackButtonNavigationCallback,
  useIsLeftNavCollapseEnabled,
  useIsLeftNavCollapsedActive,
} from '@ms/yammer-features-app-navigation';
import { useIsEngageIASimplificationPhase1WithoutReporting } from '@ms/yammer-features-engage-ia';
import { useExperimentTreatmentWithoutReporting } from '@ms/yammer-hooks/dist/experiments';
import { useIsTopLevelFeaturePage } from '@ms/yammer-hooks/dist/navigation';
import { useReportPageLoadedFromLinkClick } from '@ms/yammer-hooks/dist/pageLoad';
import { useTranslations } from '@ms/yammer-i18n-react';
import { useEndActiveSession } from '@ms/yammer-search-components';
import CommonStrings from '@ms/yammer-strings/dist/Common';
import PanesStrings from '@ms/yammer-strings/dist/Panes';
import { MultiColumnAppLayout } from '@ms/yammer-web-core-ui/dist/components/AppLayout';
import { AppLayoutBreakpoint, useContainerWidth } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { tokens } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import { usePageBackButtonTargetPath } from '@ms/yammer-web-support/dist/pageBackButton';
import {
  articleBasePath,
  articleEditPageMatchPath,
  articleNewMatchPath,
  articlePageMatchPath,
} from '@ms/yammer-web-support/dist/urls';
import type { FC, PropsWithChildren } from 'react';
import React from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

import SuiteHeader from '../SuiteHeader';

import { AppLeftNav } from './AppLeftNav';
import ArticleFooter from './LoadableArticleFooter';
import LoadableWebSubHeader from './LoadableWebSubHeader';
import { useGetBannersCallback } from './hooks';

const AppLayout: FC<PropsWithChildren> = ({ children }) => {
  'use memo';

  const { pathname } = useLocation();
  useReportPageLoadedFromLinkClick(pathname);
  useEndActiveSession();

  /**
   * This custom Article logic is temporary until we refactor CoreUI's AppLayout so
   * content/page-level templates allow slot props like `footer`.
   * https://dev.azure.com/yammer/engineering/_workitems/edit/295328
   */
  const isAnyArticlesPage = !!useRouteMatch(articleBasePath);
  const isArticleEditPage = !!useRouteMatch({ path: articleEditPageMatchPath, exact: true });
  const isArticleCreatePage = !!useRouteMatch({ path: articleNewMatchPath, exact: true });
  const isArticleThreadPage = !!useRouteMatch(articlePageMatchPath) && !isArticleEditPage && !isArticleCreatePage;
  const isSuggestedArticlePage = !!useRouteMatch({ path: '/groups/:groupId/suggested-article', exact: true });
  const backgroundColor = isAnyArticlesPage || isSuggestedArticlePage ? tokens.colorNeutralBackground1 : undefined;
  const footer = isArticleThreadPage ? <ArticleFooter /> : undefined;
  const isArticleAuthorHeaderEnabled = useExperimentTreatmentWithoutReporting('ArticleModalDialog');
  const isArticlePage = !!useRouteMatch(articlePageMatchPath);
  const width = useContainerWidth();
  const isCompact = width < AppLayoutBreakpoint.Width1032;

  const scrollPaddingTop =
    isArticleEditPage || isArticleCreatePage || (isArticleAuthorHeaderEnabled && isArticlePage) ? '120px' : '0px';
  const scrollPaddingBottom = (isArticleEditPage || isArticleCreatePage) && !isCompact ? '113px' : '0px';

  const isEngageIASimplification = useIsEngageIASimplificationPhase1WithoutReporting();
  const isLeftNavCollapsed = useIsLeftNavCollapsedActive();
  const isLeftNavCollapsible = useIsLeftNavCollapseEnabled();

  const banners = useGetBannersCallback()();

  const openAriaLabel = useTranslations(PanesStrings).showAdditionalInformationPane();
  const commonStrings = useTranslations(CommonStrings);
  const closeAriaLabel = commonStrings.commonClose();
  const backButtonAriaLabel = commonStrings.commonGoBack();

  const backTargetPath = usePageBackButtonTargetPath();
  const onBackButtonClick = useBackButtonNavigationCallback(backTargetPath ?? undefined);

  const isTopLevelFeaturePage = useIsTopLevelFeaturePage();

  return (
    <ResizableLeftNavProvider>
      <MultiColumnAppLayout
        header={
          <>
            <SuiteHeader />
            <GlobalCopilotFloatieButton />
          </>
        }
        headerBackgroundColor={tokens.colorNeutralBackground2}
        banner={banners}
        subHeader={<LoadableWebSubHeader />}
        hasBackButton={backTargetPath != null}
        backButtonAriaLabel={backButtonAriaLabel}
        onBackButtonClick={onBackButtonClick}
        hasHeroBackground={isTopLevelFeaturePage}
        leftNav={<AppLeftNav />}
        openContentSidebarAriaLabel={openAriaLabel}
        closeContentSidebarAriaLabel={closeAriaLabel}
        footer={footer}
        backgroundColor={backgroundColor}
        scrollPaddingTop={scrollPaddingTop}
        scrollPaddingBottom={scrollPaddingBottom}
        unifiedSidebar={isEngageIASimplification && !isAnyArticlesPage && !isSuggestedArticlePage}
        reservedRightRailBottomSpace={100}
        isLeftNavCollapsed={isLeftNavCollapsed}
        isLeftNavCollapsible={isLeftNavCollapsible}
      >
        {children}
      </MultiColumnAppLayout>
    </ResizableLeftNavProvider>
  );
};

export default AppLayout;
